import { useEffect, useMemo } from 'react';
import { useLazyQuery } from 'react-apollo';
import moment from 'moment';
import GET_ANALYTICS_TASKS from '../../../../core/GraphQl/Queries/GET_ANALYTICS_TASKS';
import { useTaskContext } from '../../../../core/components/tasks/useTaskContext';
import { OPEN_STATUSES } from '../configs/analytics.config';

// Build tenant-aware where clause for analytics queries
const buildAnalyticsWhere = (dateRange, filters, currentUser, projectSegment) => {
  if (!currentUser?.role?.org?.id) return null;

  const where = {
    AND: [
      // Date range filter (by created date)
      ...(dateRange && dateRange[0] && dateRange[1]
        ? [
            { createdAt_gte: dateRange[0].toISOString() },
            { createdAt_lte: dateRange[1].toISOString() },
          ]
        : []),
      // Tenant isolation
      {
        OR: [
          {
            createdBy: {
              roles_some: {
                org: {
                  OR: [
                    { parentOrg_some: { id: currentUser.role.org.id } },
                    { id: currentUser.role.org.id },
                  ],
                },
              },
            },
          },
        ],
      },
      // Dynamic filters
      ...(filters.assignees?.length
        ? [
            {
              OR: [
                { assigneeUsers_some: { id_in: filters.assignees } },
                { assigneeUser: { id_in: filters.assignees } },
              ],
            },
          ]
        : []),
      ...(filters.requestors?.length
        ? [{ createdBy: { id_in: filters.requestors } }]
        : []),
      ...(filters.requestTypes?.length
        ? [{ requestType_in: filters.requestTypes }]
        : []),
      ...(filters.statuses?.length
        ? [{ status_in: filters.statuses }]
        : []),
      ...(filters.urgencies?.length
        ? [{ urgency_in: filters.urgencies }]
        : []),
      ...(filters.advertiserTypes?.length
        ? [
            {
              createdBy: {
                roles_some: { org: { level_in: filters.advertiserTypes } },
              },
            },
          ]
        : []),
      ...(filters.advertisers?.length
        ? [
            {
              createdBy: {
                roles_some: { org: { name_in: filters.advertisers } },
              },
            },
          ]
        : []),
      // Project segment filter
      ...(projectSegment === 'projectOnly'
        ? [{ NOT: { project: null } }]
        : projectSegment === 'noProject'
          ? [{ project: null }]
          : []),
    ],
  };

  return where;
};

// Group tasks by a field and return counts
const groupBy = (tasks, field) => {
  const groups = {};
  tasks.forEach((task) => {
    const key = task[field] || 'Unknown';
    groups[key] = (groups[key] || 0) + 1;
  });
  return Object.entries(groups)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
};

// Group tasks by assignee (handles both assigneeUser and assigneeUsers)
const groupByAssignee = (tasks) => {
  const groups = {};
  tasks.forEach((task) => {
    const assignees = [];
    if (task.assigneeUsers?.length) {
      task.assigneeUsers.forEach((u) => assignees.push(u.name || 'Unknown'));
    } else if (task.assigneeUser) {
      assignees.push(task.assigneeUser.name || 'Unknown');
    } else {
      assignees.push('Unassigned');
    }
    assignees.forEach((name) => {
      groups[name] = (groups[name] || 0) + 1;
    });
  });
  return Object.entries(groups)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
};

// Group tasks by requestor (createdBy)
const groupByRequestor = (tasks) => {
  const groups = {};
  tasks.forEach((task) => {
    const name = task.createdBy?.name || 'Unknown';
    groups[name] = (groups[name] || 0) + 1;
  });
  return Object.entries(groups)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
};

// Group tasks by advertiser type (org level from createdBy)
const groupByAdvertiserType = (tasks) => {
  const groups = {};
  tasks.forEach((task) => {
    const level =
      task.createdBy?.roles?.[0]?.org?.level || 'Unknown';
    const label = level
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
    groups[label] = (groups[label] || 0) + 1;
  });
  return Object.entries(groups)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
};

// Group tasks by advertiser org name
const groupByAdvertiser = (tasks) => {
  const groups = {};
  tasks.forEach((task) => {
    const name = task.advertiser?.name || task.createdBy?.roles?.[0]?.org?.name || 'Unknown';
    groups[name] = (groups[name] || 0) + 1;
  });
  return Object.entries(groups)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
};

// Group tasks by created date for trend chart
const groupByCreatedDate = (tasks, dateRange) => {
  const dateMap = {};

  if (dateRange && dateRange[0] && dateRange[1]) {
    // Clamp start date to earliest task creation date so "All Time" doesn't
    // generate empty days before the first task existed
    let start = moment(dateRange[0]).startOf('day');
    const end = moment(dateRange[1]).startOf('day');

    if (tasks.length > 0) {
      const earliest = tasks.reduce((min, t) => {
        const d = moment(t.createdAt);
        return d.isBefore(min) ? d : min;
      }, moment(tasks[0].createdAt));
      const earliestDay = earliest.startOf('day');
      if (earliestDay.isAfter(start)) {
        start = earliestDay;
      }
    }

    const current = start.clone();
    while (current.isSameOrBefore(end)) {
      const key = current.format('YYYY-MM-DD');
      dateMap[key] = { date: key, created: 0 };
      current.add(1, 'day');
    }
  }

  tasks.forEach((task) => {
    const createdDate = moment(task.createdAt).format('YYYY-MM-DD');
    if (dateMap[createdDate]) {
      dateMap[createdDate].created += 1;
    }
  });

  return Object.values(dateMap);
};

const useAnalyticsData = (dateRange, filters = {}, slaConfigMap = {}, projectSegment = 'all') => {
  const { currentUser } = useTaskContext();

  const [fetchTasks, { data, loading }] = useLazyQuery(GET_ANALYTICS_TASKS, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  // Fetch on dateRange/filter changes
  useEffect(() => {
    if (!currentUser?.role?.org?.id) return;

    const where = buildAnalyticsWhere(dateRange, filters, currentUser, projectSegment);
    if (!where) return;

    fetchTasks({
      variables: {
        where,
        orderBy: 'createdAt_DESC',
        first: 10000,
      },
    });
  }, [dateRange, filters, currentUser, fetchTasks, projectSegment]);

  // Extract raw tasks from connection
  const rawTasks = useMemo(() => {
    if (!data?.tasksConnection?.edges) return [];
    return data.tasksConnection.edges.map((e) => e.node);
  }, [data]);

  // Aggregate metrics
  const metrics = useMemo(() => {
    if (!rawTasks.length) {
      return {
        total: 0,
        open: 0,
        completed: 0,
        overdue: 0,
        byStatus: [],
        byAssignee: [],
        byRequestor: [],
        byRequestType: [],
        byUrgency: [],
        byAdvertiserType: [],
        byAdvertiser: [],
        trend: [],
      };
    }

    const now = moment();
    const overdue = rawTasks.filter(
      (t) =>
        t.dueDate &&
        moment(t.dueDate).isBefore(now) &&
        OPEN_STATUSES.includes(t.status)
    ).length;

    return {
      total: rawTasks.length,
      open: rawTasks.filter((t) => OPEN_STATUSES.includes(t.status)).length,
      completed: rawTasks.filter((t) => t.status === 'COMPLETED').length,
      overdue,
      byStatus: groupBy(rawTasks, 'status'),
      byAssignee: groupByAssignee(rawTasks),
      byRequestor: groupByRequestor(rawTasks),
      byRequestType: groupBy(rawTasks, 'requestType'),
      byUrgency: groupBy(rawTasks, 'urgency'),
      byAdvertiserType: groupByAdvertiserType(rawTasks),
      byAdvertiser: groupByAdvertiser(rawTasks),
      trend: groupByCreatedDate(rawTasks, dateRange),
    };
  }, [rawTasks, dateRange]);

  const totalCount = data?.tasksConnection?.aggregate?.count || 0;

  // Derive filter dropdown options from actual task data
  const filterOptions = useMemo(() => {
    if (!rawTasks.length) return { assignees: [], requestors: [], advertisers: [] };

    const assigneeMap = {};
    const requestorMap = {};
    const advertiserMap = {};

    rawTasks.forEach((task) => {
      // Assignees - from assigneeUsers or assigneeUser
      if (task.assigneeUsers?.length) {
        task.assigneeUsers.forEach((u) => {
          if (u.id && u.name) assigneeMap[u.id] = { id: u.id, name: u.name };
        });
      } else if (task.assigneeUser?.id && task.assigneeUser?.name) {
        assigneeMap[task.assigneeUser.id] = { id: task.assigneeUser.id, name: task.assigneeUser.name };
      }

      // Requestors - from createdBy
      if (task.createdBy?.id && task.createdBy?.name) {
        requestorMap[task.createdBy.id] = { id: task.createdBy.id, name: task.createdBy.name };
      }

      // Advertisers - from channelPartner on the task
      if (task.channelPartner?.name) {
        advertiserMap[task.channelPartner.name] = { name: task.channelPartner.name };
      }
    });

    return {
      assignees: Object.values(assigneeMap).sort((a, b) => a.name.localeCompare(b.name)),
      requestors: Object.values(requestorMap).sort((a, b) => a.name.localeCompare(b.name)),
      advertisers: Object.values(advertiserMap).sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [rawTasks]);

  return { metrics, loading, rawTasks, totalCount, filterOptions };
};

export default useAnalyticsData;
